import type { FC, ReactNode } from "react";
import { Button, MenuTrigger } from "react-aria-components";
import { twMerge } from "tailwind-merge";
import { SimpleMenu } from "../Menu/simpleMenu.js";
import { SimpleMenuItem } from "../Menu/simpleMenuItem.js";
import { SimpleMenuPopover } from "../Menu/simpleMenuPopover.js";
import {
  TeammateLabel,
  type TeammateLabelProps,
} from "../teammate/teammateLabel.js";

export interface CustomerTimelineCallParticipant extends TeammateLabelProps {
  id: string;
  gradingSummary?: ReactNode;
  outcome?: ReactNode;
}

export interface CustomerTimelineCallProps {
  title: ReactNode;
  participants: readonly CustomerTimelineCallParticipant[];
  duration?: string;
  /**
   * Pressing the title or duration opens the call; with several participants,
   * a menu first asks whose grading to open.
   */
  openCall: (participantId: string) => void;
}

/** Recording facts for a single call, with no data fetching or route knowledge. */
export const CustomerTimelineCall: FC<CustomerTimelineCallProps> = (props) => {
  const { title, participants, duration, openCall } = props;
  const [onlyParticipant] = participants;
  const hasMultipleParticipants = participants.length > 1;
  // With several participants the menu opens the call instead.
  const openOnlyParticipant =
    onlyParticipant && !hasMultipleParticipants
      ? () => openCall(onlyParticipant.id)
      : undefined;
  const hasGradingMetadata = participants.some(
    (participant) =>
      participant.gradingSummary != null || participant.outcome != null,
  );

  // The no-break space keeps the duration on the title's last line. That only
  // holds while both flow as one line of text, so the button wraps both.
  const titleButton = (
    <Button
      className="group block cursor-pointer rounded-sm text-left text-sm font-medium text-foreground focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
      onPress={openOnlyParticipant}
    >
      <span className="whitespace-nowrap">
        <span className="whitespace-normal underline decoration-gray-300 underline-offset-2 transition-colors group-hover:decoration-gray-500">
          {title}
        </span>
        {duration && (
          <span className="text-xs font-normal text-muted-foreground">
            {"\u00a0\u00a0"}
            {duration}
          </span>
        )}
      </span>
    </Button>
  );

  return (
    <div className="max-w-2xl min-w-0">
      {hasMultipleParticipants ? (
        <MenuTrigger>
          {titleButton}
          <SimpleMenuPopover placement="bottom start">
            <SimpleMenu aria-label="Choose teammate grading">
              {participants.map((participant) => (
                <SimpleMenuItem
                  key={participant.id}
                  id={participant.id}
                  onAction={() => openCall(participant.id)}
                >
                  {participant.name}
                </SimpleMenuItem>
              ))}
            </SimpleMenu>
          </SimpleMenuPopover>
        </MenuTrigger>
      ) : (
        titleButton
      )}

      {participants.length > 0 && (
        <div
          className={twMerge(
            "mt-2 flex text-xs text-muted-foreground",
            hasGradingMetadata
              ? "flex-col gap-y-2"
              : "flex-wrap gap-x-3 gap-y-1",
          )}
        >
          {participants.map((participant) => (
            <div
              key={participant.id}
              className="flex flex-wrap items-center gap-x-3 gap-y-1"
            >
              <TeammateLabel
                name={participant.name}
                kind={participant.kind}
                isAvatarHidden={participant.isAvatarHidden}
              />
              {participant.gradingSummary}
              {participant.outcome}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
